import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title?: string
  message: string
  duration?: number
}

export const useToastStore = defineStore('toast', () => {
  const toasts = ref<Toast[]>([])

  function addToast(toast: Omit<Toast, 'id'>) {
    const id = `toast-${Date.now()}-${Math.random()}`
    const newToast: Toast = {
      id,
      ...toast,
      duration: toast.duration ?? 5000
    }

    toasts.value.push(newToast)

    // Auto-remove after duration
    if (newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, newToast.duration)
    }

    return id
  }

  function removeToast(id: string) {
    const index = toasts.value.findIndex(t => t.id === id)
    if (index !== -1) {
      toasts.value.splice(index, 1)
    }
  }

  function success(message: string, title?: string, duration?: number) {
    return addToast({ type: 'success', message, title, duration })
  }

  function error(message: string, title?: string, duration?: number) {
    return addToast({ type: 'error', message, title, duration })
  }

  function warning(message: string, title?: string, duration?: number) {
    return addToast({ type: 'warning', message, title, duration })
  }

  function info(message: string, title?: string, duration?: number) {
    return addToast({ type: 'info', message, title, duration })
  }

  function clear() {
    toasts.value = []
  }

  return {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
    clear
  }
})
